import { useState } from 'react';
import { DollarSign, Heart, Loader2, Percent } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { tipAPI } from '../lib/api';
import { toast } from 'sonner';

const PRESET_AMOUNTS = [2, 5, 10];
const PRESET_PERCENTAGES = [10, 15, 20];

const TipDialog = ({ open, onOpenChange, job }) => {
  const [mode, setMode] = useState('amount');
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const getAmount = () => {
    if (selectedPreset !== null) {
      if (mode === 'amount') return selectedPreset;
      return Math.round((job.price * selectedPreset / 100) * 100) / 100;
    }
    return parseFloat(customAmount) || 0;
  };

  const tipAmount = getAmount();

  const handleSubmit = async () => {
    if (tipAmount < 1) {
      toast.error('Minimum tip is $1.00');
      return;
    }
    setSubmitting(true);
    try {
      const originUrl = window.location.origin;
      const response = await tipAPI.createCheckout(job.id, tipAmount, originUrl);
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process tip');
      setSubmitting(false);
    }
  };

  const selectPreset = (val) => {
    setSelectedPreset(val);
    setCustomAmount('');
  };

  const handleCustomChange = (val) => {
    setCustomAmount(val);
    setSelectedPreset(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Heart className="w-5 h-5 text-pink-500" />
            Tip {job?.driver_name || 'Driver'}
          </DialogTitle>
          <DialogDescription>
            Show your appreciation for a great {job?.job_type === 'ride' ? 'ride' : 'delivery'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          {/* Mode toggle */}
          <div className="flex bg-zinc-100 rounded-lg p-1">
            <button
              onClick={() => { setMode('amount'); setSelectedPreset(null); setCustomAmount(''); }}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md text-sm font-medium transition-colors ${mode === 'amount' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500'}`}
              data-testid="tip-mode-amount"
            >
              <DollarSign className="w-3.5 h-3.5" /> Amount
            </button>
            <button
              onClick={() => { setMode('percent'); setSelectedPreset(null); setCustomAmount(''); }}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md text-sm font-medium transition-colors ${mode === 'percent' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500'}`}
              data-testid="tip-mode-percent"
            >
              <Percent className="w-3.5 h-3.5" /> Percentage
            </button>
          </div>

          {/* Preset options */}
          <div className="grid grid-cols-3 gap-3">
            {(mode === 'amount' ? PRESET_AMOUNTS : PRESET_PERCENTAGES).map((val) => {
              const isSelected = selectedPreset === val;
              const display = mode === 'amount' ? `$${val}` : `${val}%`;
              const subtext = mode === 'percent' ? `$${(job.price * val / 100).toFixed(2)}` : null;
              return (
                <button
                  key={val}
                  onClick={() => selectPreset(val)}
                  className={`py-3 rounded-xl border-2 text-center transition-all ${
                    isSelected
                      ? 'border-orange-500 bg-orange-50 text-orange-700'
                      : 'border-zinc-200 bg-white text-zinc-700 hover:border-zinc-300'
                  }`}
                  data-testid={`tip-preset-${val}`}
                >
                  <span className="text-lg font-bold">{display}</span>
                  {subtext && <p className="text-xs text-zinc-400 mt-0.5">{subtext}</p>}
                </button>
              );
            })}
          </div>

          {/* Custom amount */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-zinc-600">Custom amount</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 font-medium">$</span>
              <Input
                type="number"
                step="0.01"
                min="1"
                max="500"
                placeholder="Enter amount"
                value={customAmount}
                onChange={(e) => handleCustomChange(e.target.value)}
                className="pl-7 h-11"
                data-testid="tip-custom-input"
              />
            </div>
          </div>

          {/* Summary */}
          {tipAmount > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center justify-between" data-testid="tip-summary">
              <span className="text-sm text-green-700">Tip amount</span>
              <span className="text-xl font-bold text-green-700">${tipAmount.toFixed(2)}</span>
            </div>
          )}

          {/* Submit */}
          <Button
            onClick={handleSubmit}
            disabled={tipAmount < 1 || submitting}
            className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-xl text-base font-medium"
            data-testid="tip-submit-btn"
          >
            {submitting ? (
              <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Processing...</>
            ) : (
              <><Heart className="w-5 h-5 mr-2" /> Send ${tipAmount > 0 ? tipAmount.toFixed(2) : '0.00'} Tip</>
            )}
          </Button>

          <p className="text-xs text-center text-zinc-400">
            100% of your tip goes directly to {job?.driver_name || 'the driver'}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TipDialog;
